import { z } from "zod";

// Mirror lib/gpt_live_exercises.py. This optional payload never changes legacy boards.
const short = z.string().trim().min(1).max(160);
const text = z.string().trim().min(1).max(500);
const base = {
  title: z.string().trim().min(1).max(120),
  instruction: text,
  objective: short,
  level: z.enum(["A1", "A2", "B1", "B2", "C1", "C2"]),
  hint: z.string().trim().max(350).default(""),
};
const row = z
  .object({
    subject: short,
    form: short,
    blank: z.boolean(),
    emphasis: z.string().trim().max(80).default(""),
  })
  .strict();
const gap = z
  .object({
    sentence: text,
    options: z.array(short).min(2).max(4),
    answer: short,
  })
  .strict();

export const StudyExerciseSchema = z
  .discriminatedUnion("family", [
    z
      .object({
        ...base,
        family: z.literal("reading"),
        story: z.string().trim().min(40).max(1800),
        focus: z.array(short).max(4).default([]),
        vocabulary: z
          .array(z.object({ term: short, meaning: short }).strict())
          .max(4)
          .default([]),
        question: text,
      })
      .strict(),
    z
      .object({ ...base, family: z.literal("conjugation"), rows: z.array(row).min(2).max(6) })
      .strict(),
    z
      .object({ ...base, family: z.literal("gap_fill"), items: z.array(gap).min(1).max(4) })
      .strict(),
    z
      .object({
        ...base,
        family: z.literal("word_tiles"),
        prompt: text,
        tiles: z.array(short).min(2).max(12),
      })
      .strict(),
  ])
  .superRefine((exercise, ctx) => {
    const invalid = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    if (exercise.family === "reading") {
      if (
        exercise.focus.some((term) => !exercise.story.includes(term)) ||
        exercise.vocabulary.some((note) => !exercise.story.includes(note.term))
      )
        invalid("Reading references must occur in the story.");
    } else if (exercise.family === "conjugation") {
      if (!exercise.rows.some((row) => row.blank)) invalid("Include at least one blank.");
      if (new Set(exercise.rows.map((row) => row.subject)).size !== exercise.rows.length)
        invalid("Row subjects must be unique.");
      if (exercise.rows.some((row) => row.emphasis && !row.form.includes(row.emphasis)))
        invalid("Emphasis must occur in the form.");
    } else if (exercise.family === "gap_fill") {
      for (const item of exercise.items) {
        if ((item.sentence.match(/_{3,}/g) ?? []).length !== 1)
          invalid("Each sentence needs exactly one gap.");
        if (
          !item.options.includes(item.answer) ||
          new Set(item.options).size !== item.options.length
        )
          invalid("Options must be unique and include the answer.");
      }
    }
  });

export type StudyExercise = z.infer<typeof StudyExerciseSchema>;
